import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import api from '../lib/api';
import { formatApiError } from '../lib/format';

const SA_MOBILE = /^(\+27|0)[6-8][0-9]{8}$/;

export default function Signup() {
  const [form, setForm] = useState({
    first_name: '',
    surname: '',
    mobile: '',
    age: '',
    email: '',
    password: '',
    confirm: '',
  });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const ageNum = parseInt(form.age || '0', 10);
  const ageBlocked = form.age !== '' && (isNaN(ageNum) || ageNum < 18);

  const submit = async (e) => {
    e.preventDefault();
    if (form.password !== form.confirm) {
      toast.error('Passwords do not match');
      return;
    }
    if (form.password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    if (ageBlocked) {
      toast.error('You must be 18 or older to sign up');
      return;
    }
    if (!SA_MOBILE.test(form.mobile.replace(/\s/g, ''))) {
      toast.error('Invalid SA mobile number (e.g. 0821234567 or +27821234567)');
      return;
    }
    setLoading(true);
    try {
      await api.post('/auth/signup', {
        first_name: form.first_name,
        surname: form.surname,
        mobile: form.mobile.replace(/\s/g, ''),
        age: ageNum,
        email: form.email,
        password: form.password,
      });
      toast.success('Account created — check your email');
      navigate(`/check-email?email=${encodeURIComponent(form.email)}`);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const Input = ({ label, testid, value, onChange, ...rest }) => (
    <div>
      <label className="text-xs uppercase tracking-[0.2em] text-[#C0C0C0]/80">{label}</label>
      <input
        data-testid={testid}
        value={value}
        onChange={onChange}
        className="mt-2 w-full bg-[#0a0a0a] border border-[#D4AF37]/30 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-[#D4AF37]"
        {...rest}
      />
    </div>
  );

  return (
    <div data-testid="signup-page" className="max-w-xl mx-auto px-4 sm:px-6 py-14">
      <div className="card-embossed p-8 sm:p-10">
        <p className="text-xs uppercase tracking-[0.32em] text-[#D4AF37] font-semibold mb-3">Join Golden Fish</p>
        <h1 className="font-serif-display text-3xl sm:text-4xl font-black text-gold-gradient">Create your account</h1>
        <p className="mt-2 text-sm text-[#C0C0C0]">18+ SA residents only.</p>

        <form onSubmit={submit} className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Input label="First name" testid="signup-firstname" required value={form.first_name} onChange={set('first_name')} />
          <Input label="Surname" testid="signup-surname" required value={form.surname} onChange={set('surname')} />
          <Input label="Mobile (SA)" testid="signup-mobile" required value={form.mobile} onChange={set('mobile')} placeholder="0821234567" />
          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-[#C0C0C0]/80">Age</label>
            <input
              data-testid="signup-age"
              type="number"
              min={1}
              max={120}
              required
              value={form.age}
              onChange={set('age')}
              className={`mt-2 w-full bg-[#0a0a0a] rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-[#D4AF37] border ${
                ageBlocked ? 'border-[#991B1B]' : 'border-[#D4AF37]/30'
              }`}
            />
            {ageBlocked && (
              <p data-testid="age-error" className="mt-1 text-xs text-[#F87171]">
                You must be 18 or older to sign up.
              </p>
            )}
          </div>
          <div className="sm:col-span-2">
            <Input label="Email" testid="signup-email" type="email" required value={form.email} onChange={set('email')} />
          </div>
          <Input label="Password" testid="signup-password" type="password" required minLength={8} value={form.password} onChange={set('password')} />
          <Input label="Confirm password" testid="signup-confirm" type="password" required minLength={8} value={form.confirm} onChange={set('confirm')} />

          <button
            type="submit"
            data-testid="signup-submit"
            disabled={loading || ageBlocked}
            className="btn-gold sm:col-span-2 mt-2 inline-flex items-center justify-center gap-2"
          >
            {loading && <Loader2 className="animate-spin" size={18} />}
            Create account
          </button>
        </form>

        <p className="mt-6 text-sm text-[#C0C0C0] text-center">
          Already have an account?{' '}
          <Link to="/login" className="text-[#D4AF37]">Login</Link>
        </p>
      </div>
    </div>
  );
}
